import { Injectable } from '@angular/core';
import { Observable, of, throwError, BehaviorSubject } from 'rxjs';
import { map, switchMap, catchError, tap, timeout } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { AuthenticationResult } from '@azure/msal-browser';

import { AuthRepository } from './auth.repository';
import { MsalService } from '../../../core/services/msal.service';
import { environment } from '../../../../environments/environment';
import {
  AuthTokens,
  LoginRequest,
  LogoutRequest,
  UserModel,
  AuthState,
  AuthError,
  AuthErrorType,
  initialAuthState,
  createBasicUser
} from '../models';
import { loginRequest } from '../../../core/config/msal.config';

/**
 * Implementação do repositório de autenticação
 * Baseado no auth_repository_impl.dart do Flutter
 */
@Injectable({
  providedIn: 'root'
})
export class AuthRepositoryImpl extends AuthRepository {
  private authStateSubject = new BehaviorSubject<AuthState>(initialAuthState);

  readonly authState$ = this.authStateSubject.asObservable();

  constructor(
    private msalService: MsalService,
    private http: HttpClient
  ) {
    super();
    // Remover inicialização automática - será feita pelo AppInitializer
  }

  /**
   * Inicializa o estado de autenticação após MSAL estar pronto
   */
  initializeAuthState(): void {
    this.msalService.isAuthenticated().subscribe((isAuthenticated: boolean) => {
      if (isAuthenticated) {
        // IMPORTANTE: Subscribir no observable para que seja executado
        this.loadUserData().subscribe({
          next: (user) => {

          },
          error: (error) => {
            console.error('❌ [AUTH] Erro ao carregar dados do usuário');
          }
        });
      }
    });
  }

  /**
   * Inicializa o MSAL
   */
  initializeMsal(): Observable<boolean> {
    this.updateAuthState({ isLoading: true, error: null });

    return this.msalService.initialize().pipe(
      switchMap(initialized => {
        if (!initialized) {
          this.updateAuthState({
            isLoading: false,
            error: 'Falha ao inicializar autenticação'
          });
          return of(false);
        }

        // Após inicializar, verificar se há uma sessão ativa
        return this.verificarSessaoExistente().pipe(
          tap(hasSession => {

            this.updateAuthState({ isLoading: false });
          }),
          map(() => true),
          catchError(error => {
            console.warn('⚠️ Erro ao verificar sessão existente');
            this.updateAuthState({ isLoading: false });
            return of(true); // Ainda considera inicializado mesmo sem sessão
          })
        );
      }),
      catchError(error => {
        this.updateAuthState({
          isLoading: false,
          error: this.createAuthError(AuthErrorType.INITIALIZATION_ERROR, error).message
        });
        return of(false);
      })
    );
  }

  /**
   * Verifica se existe uma sessão ativa após reload
   */
  private verificarSessaoExistente(): Observable<boolean> {
    try {
      const accounts = this.msalService.getAllAccounts();

      if (accounts.length === 0) {
        return of(false);
      }

      // Definir a conta ativa
      this.msalService.setActiveAccount(accounts[0]);

      // A existência de uma conta no cache não garante que a sessão ainda é válida.
      // O token silencioso é renovado e validado antes de carregar o usuário.
      return this.validateSession().pipe(
        switchMap(isValid => {
          if (!isValid) {
            return this.clearSession().pipe(map(() => false));
          }

          return this.loadUserData().pipe(
            map(user => {
              if (user) {
                this.updateAuthState({
                  user,
                  isAuthenticated: true,
                  lastActivity: new Date()
                });
                return true;
              }
              return false;
            })
          );
        }),
        catchError(() => this.clearSession().pipe(map(() => false)))
      );
    } catch (error) {
      return of(false);
    }
  }

  /**
   * Realiza login
   */
  login(request: LoginRequest = {}): Observable<AuthTokens> {
    this.updateAuthState({ isLoading: true, error: null });

    const popupRequest = {
      scopes: request.scopes || ['openid', 'profile', 'email', 'offline_access'],
      prompt: request.prompt,
      extraQueryParameters: request.extraQueryParameters
    };

    return this.msalService.loginPopup(popupRequest).pipe(
      switchMap((authResult: AuthenticationResult) => {
        const tokens: AuthTokens = {
          accessToken: authResult.accessToken,
          idToken: authResult.idToken || '',
          refreshToken: '',
          expiresIn: authResult.expiresOn ?
            Math.floor((authResult.expiresOn.getTime() - Date.now()) / 1000) : 3600,
          tokenType: 'Bearer',
          scopes: authResult.scopes || []
        };

        this.updateAuthState({
          tokens,
          isAuthenticated: true,
          lastActivity: new Date()
        });

        return this.loadUserData().pipe(
          map(() => tokens)
        );
      }),
      catchError(error => {
        const authError = this.createAuthError(AuthErrorType.LOGIN_ERROR, error);
        this.updateAuthState({
          isLoading: false,
          error: authError.message
        });
        return throwError(() => authError);
      })
    );
  }

  /**
   * Realiza logout
   */
  logout(request?: LogoutRequest): Observable<void> {
    this.updateAuthState({ isLoading: true });

    return this.msalService.logout().pipe(
      tap(() => {
        this.updateAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          tokens: null,
          error: null,
          lastActivity: null
        });
      }),
      catchError(error => {
        const authError = this.createAuthError(AuthErrorType.LOGOUT_ERROR, error);
        this.updateAuthState({
          isLoading: false,
          error: authError.message
        });
        return throwError(() => authError);
      })
    );
  }

  /**
   * Atualiza token
   */
  refreshToken(): Observable<AuthTokens> {
    const account = this.msalService.getActiveAccount();
    if (!account) {
      return throwError(() => new Error('Nenhuma conta ativa'));
    }

    return this.msalService.acquireTokenSilent({
      // Validar o mesmo recurso usado pelas requisições do backoffice.
      scopes: loginRequest.scopes,
      account
    }).pipe(
      map((authResult: AuthenticationResult) => ({
        accessToken: authResult.accessToken,
        idToken: authResult.idToken || '',
        refreshToken: '',
        expiresIn: authResult.expiresOn ?
          Math.floor((authResult.expiresOn.getTime() - Date.now()) / 1000) : 3600,
        tokenType: 'Bearer',
        scopes: authResult.scopes || []
      })),
      catchError(error => {
        const authError = this.createAuthError(AuthErrorType.TOKEN_ERROR, error);
        return throwError(() => authError);
      })
    );
  }

  /**
   * Verifica se está autenticado
   */
  isAuthenticated(): Observable<boolean> {
    return this.msalService.isAuthenticated();
  }

  /**
   * Obtém usuário atual
   */
  getCurrentUser(): Observable<UserModel | null> {
    return this.authState$.pipe(
      map(state => state.user)
    );
  }

  /**
   * Obtém access token
   */
  getAccessToken(): Observable<string | null> {
    return this.msalService.getAccessToken().pipe(
      catchError(() => of(null))
    );
  }

  /**
   * Limpa sessão
   */
  clearSession(): Observable<void> {
    try {
      // Impede que a conta em cache seja considerada autenticada novamente
      // durante a inicialização ou após uma resposta 401.
      this.msalService.setActiveAccount(null);
    } catch {
      // A instância pode ainda não estar inicializada.
    }
    this.updateAuthState(initialAuthState);
    return of(void 0);
  }

  /**
   * Valida sessão
   */
  validateSession(): Observable<boolean> {
    return this.isAuthenticated().pipe(
      switchMap(isAuth => {
        if (!isAuth) {
          return of(false);
        }

        return this.refreshToken().pipe(
          // A renovação silenciosa valida somente o cache/conta do MSAL.
          // Esta chamada confirma que o backend também aceita a sessão.
          switchMap(() => this.http.get<void>(
            `${environment.FI_FINANC_IMOB_BE}sessao/validar`,
            { observe: 'response' }
          )),
          timeout(5000),
          map(response => response.status >= 200 && response.status < 300),
          catchError((error: unknown) => {
            const status = typeof error === 'object' && error !== null && 'status' in error
              ? error.status
              : undefined;
            const errorCode = typeof error === 'object' && error !== null && 'errorCode' in error
              ? error.errorCode
              : undefined;

            // O token é válido, mas a role básica não foi atribuída. Isso é
            // autorização insuficiente, não uma sessão inválida: preserve a
            // conta para exibir a orientação de acesso ao usuário.
            if (status === 403) {
              return of(true);
            }

            console.warn('[AUTH] Falha na validação da sessão', {
              status,
              errorCode: typeof errorCode === 'string' ? errorCode : undefined,
              reason: error instanceof Error ? error.name : 'unknown'
            });

            return of(false);
          })
        );
      })
    );
  }

  /**
   * Carrega dados do usuário
   */
  private loadUserData(): Observable<UserModel> {
    const account = this.msalService.getActiveAccount();
    if (!account) {
      return throwError(() => new Error('Nenhuma conta ativa'));
    }

    // Buscar dados do usuário via Microsoft Graph ou API própria
    return this.getUserFromGraph(account).pipe(
      tap(user => {
        this.updateAuthState({
          user,
          isLoading: false,
          isAuthenticated: true
        });
      }),
      catchError(error => {
        // Não usar o fallback quando a sessão foi rejeitada pelo recurso protegido.
        if (error?.status === 401 || error?.status === 403) {
          return throwError(() => error);
        }

        // Fallback: criar usuário básico com dados da conta MSAL
        const basicUser = createBasicUser(account);
        return of(basicUser).pipe(
          tap(user => {
            this.updateAuthState({
              user,
              isLoading: false,
              isAuthenticated: true
            });
          })
        );
      })
    );
  }

  /**
   * Obtém usuário do Microsoft Graph
   * Inclui displayName e onPremisesExtensionAttributes via $select
   */
  private getUserFromGraph(account: any): Observable<UserModel> {
    const graphUrl = 'https://graph.microsoft.com/v1.0/me?$select=displayName,givenName,surname,mail,userPrincipalName,id,jobTitle,department,officeLocation,mobilePhone,businessPhones,onPremisesSamAccountName,onPremisesExtensionAttributes';
    return this.msalService.getAccessToken(['https://graph.microsoft.com/User.Read']).pipe(
      switchMap(token => {
        return this.http.get<any>(graphUrl, {
          headers: { Authorization: `Bearer ${token}` },
          observe: 'response'
        });
      }),
      switchMap(graphResponse => {
        const graphUser = graphResponse.body;
        const userId = encodeURIComponent(graphUser.id);
        const userByIdUrl = `https://graph.microsoft.com/v1.0/users/${userId}?$select=displayName,onPremisesSamAccountName,onPremisesExtensionAttributes`;

        return this.msalService.getAccessToken(['https://graph.microsoft.com/User.Read']).pipe(
          switchMap(token => this.http.get<any>(userByIdUrl, {
            headers: { Authorization: `Bearer ${token}` },
            observe: 'response'
          })),
          map(userByIdResponse => {
            const userById = userByIdResponse.body;
            return {
              ...graphUser,
              onPremisesExtensionAttributes: userById.onPremisesExtensionAttributes ?? graphUser.onPremisesExtensionAttributes
            };
          }),
          catchError(error => {
            console.warn('[AuthRepository] Não foi possível consultar /users/{id}; mantendo resposta de /me');
            return of(graphUser);
          })
        );
      }),
      map(graphUser => {
        const authorizationUser = createBasicUser(account);
        return {
          ...this.mapGraphUserToModel(graphUser),
          roles: authorizationUser.roles,
          groups: authorizationUser.groups
        };
      }),
      catchError(error => {
        console.warn('Erro ao buscar dados do Graph');
        return throwError(() => error);
      })
    );
  }

  /**
   * Mapeia usuário do Graph para modelo interno
   */
  private mapGraphUserToModel(graphUser: any): UserModel {
    const onPremisesExtensionAttributes: Record<string, string | null> =
      graphUser.onPremisesExtensionAttributes ?? {};
    const gerencia = onPremisesExtensionAttributes['extensionAttribute1'] ?? undefined;
    const diretoria = onPremisesExtensionAttributes['extensionAttribute2'] ?? undefined;
    const nucleo = graphUser.department ?? undefined;

    const userModel = {
      id: graphUser.id,
      displayName: graphUser.displayName,
      givenName: graphUser.givenName,
      surname: graphUser.surname,
      mail: graphUser.mail,
      userPrincipalName: graphUser.userPrincipalName,
      jobTitle: graphUser.jobTitle,
      department: graphUser.department,
      officeLocation: graphUser.officeLocation,
      mobilePhone: graphUser.mobilePhone,
      businessPhones: graphUser.businessPhones ?? [],
      onPremisesSamAccountName: graphUser.onPremisesSamAccountName,
      onPremisesExtensionAttributes,
      gerencia,
      diretoria,
      nucleo,
      samAccountName: graphUser.onPremisesSamAccountName,
      name: graphUser.displayName || graphUser.userPrincipalName,
      email: graphUser.mail || graphUser.userPrincipalName,
      username: graphUser.onPremisesSamAccountName || graphUser.userPrincipalName,
      roles: [],
      permissions: [],
      tenantId: '',
      lastLogin: new Date(),
      isActive: true,
      profile: {
        firstName: graphUser.givenName || '',
        lastName: graphUser.surname || '',
        displayName: graphUser.displayName || graphUser.userPrincipalName,
        jobTitle: graphUser.jobTitle,
        department: graphUser.department,
        officeLocation: graphUser.officeLocation,
        phoneNumber: graphUser.mobilePhone || graphUser.businessPhones?.[0],
        gerencia,
        diretoria,
        nucleo
      },
      extensionAttributes: onPremisesExtensionAttributes
    };

    return userModel;
  }

  /**
   * Atualiza estado de autenticação
   */
  private updateAuthState(partialState: Partial<AuthState>): void {
    const currentState = this.authStateSubject.value;
    const newState = { ...currentState, ...partialState };


    this.authStateSubject.next(newState);
  }

  /**
   * Cria erro estruturado de autenticação
   */
  private createAuthError(type: AuthErrorType, error: any): AuthError {
    let message = 'Erro desconhecido de autenticação';
    let code = undefined;

    if (error?.errorCode) {
      code = error.errorCode;
      switch (error.errorCode) {
        case 'user_cancelled':
          message = 'Login cancelado pelo usuário';
          break;
        case 'consent_required':
          message = 'Consentimento necessário';
          break;
        case 'interaction_required':
          message = 'Interação do usuário necessária';
          break;
        default:
          message = error.errorMessage || 'Erro de autenticação';
      }
    } else if (error?.message) {
      message = error.message;
    }

    return {
      type,
      message,
      code,
      details: error
    };
  }
}
